import React, { useState, useEffect } from 'react';
import Navigation from './Navigation';
import API_BASE_URL from '../config/api';
import '../styles/TodoList.css';

const TodoList = ({ user }) => {
  const [activeTab, setActiveTab] = useState('Dates');
  const [todos, setTodos] = useState({ Dates: [], Movies: [], Goals: [] });
  const [newTodo, setNewTodo] = useState('');

  useEffect(() => {
    fetchTodos();
  }, []);

  const fetchTodos = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/todos`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTodos(data);
      }
    } catch (error) {
      console.error('Error fetching todos:', error);
    }
  };

  const addTodo = async () => {
    if (!newTodo.trim()) return;
    try {
      const response = await fetch(`${API_BASE_URL}/api/todos`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ text: newTodo.trim(), category: activeTab })
      });
      if (response.ok) {
        const created = await response.json();
        setTodos(prev => ({
          ...prev,
          [activeTab]: [created, ...prev[activeTab]]
        }));
        setNewTodo('');
      }
    } catch (error) {
      console.error('Error adding todo:', error);
    }
  };

  const toggleComplete = async (todo) => {
    const confirmMsg = todo.completed
      ? 'Mark this task as incomplete?'
      : 'Mark this task as completed?';
    if (!window.confirm(confirmMsg)) return;

    // Optimistic UI update
    setTodos(prev => ({
      ...prev,
      [todo.category]: prev[todo.category].map(t =>
        t._id === todo._id ? { ...t, completed: !t.completed } : t
      )
    }));

    try {
      const response = await fetch(`${API_BASE_URL}/api/todos/${todo._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ completed: !todo.completed })
      });
      if (!response.ok) throw new Error('Failed to update');
    } catch (error) {
      console.error('Error toggling todo:', error);
      // Revert on error
      setTodos(prev => ({
        ...prev,
        [todo.category]: prev[todo.category].map(t =>
          t._id === todo._id ? { ...t, completed: todo.completed } : t
        )
      }));
    }
  };

  const deleteTodo = async (todo) => {
    if (!window.confirm('Delete this task permanently?')) return;

    // Optimistic UI removal
    setTodos(prev => ({
      ...prev,
      [todo.category]: prev[todo.category].filter(t => t._id !== todo._id)
    }));

    try {
      const response = await fetch(`${API_BASE_URL}/api/todos/${todo._id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (!response.ok) throw new Error('Failed to delete');
    } catch (error) {
      console.error('Error deleting todo:', error);
      // Re-fetch on error to restore state
      fetchTodos();
    }
  };

  return (
    <div className="todo-list">
      <div className="container">
        <div className="page-header">
          <h1>Our Shared To-Do List</h1>
          <p className="subtitle">Planning our future together, one task at a time ❤️</p>
        </div>

        <div className="category-tabs">
          {['Dates','Movies','Goals'].map(tab => (
            <button
              key={tab}
              className={`tab ${activeTab===tab?'active':''}`}
              onClick={() => setActiveTab(tab)}
            >
              {tab==='Dates'?'💕':''}
              {tab==='Movies'?'🎬':''}
              {tab==='Goals'?'🎯':''}
              {tab}
            </button>
          ))}
        </div>

        <div className="add-todo">
          <input
            type="text"
            placeholder={`Add a new ${activeTab.toLowerCase().slice(0,-1)}...`}
            value={newTodo}
            onChange={e=>setNewTodo(e.target.value)}
            onKeyPress={e=>e.key==='Enter'&&addTodo()}
          />
          <button onClick={addTodo} className="add-btn">+</button>
        </div>

        <div className="todos-container">
          <div className="section-info">
            <h3>{activeTab}</h3>
            <span className="count">
              {todos[activeTab].length} total • {todos[activeTab].filter(t=>t.completed).length} completed
            </span>
          </div>
          {todos[activeTab].length>0 ? (
            <div className="todos-list">
              {todos[activeTab].map(todo=>(
                <div key={todo._id} className="todo-item">
                  <input
                    type="checkbox"
                    checked={todo.completed}
                    onChange={()=>toggleComplete(todo)}
                  />
                  <span className={todo.completed?'completed-text':''}>{todo.text}</span>
                  <button
                    className="delete-btn"
                    onClick={()=>deleteTodo(todo)}
                  >
                    🗑️
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <div className="heart-icon">❤️</div>
              <p>
                {activeTab==='Dates'
                  ?"No date plans yet? Let's create some romantic memories! 💕"
                  :activeTab==='Movies'
                    ?"No movies yet? Let's pick our watchlist! 🎬"
                    :"No goals yet? Let's dream together! 🎯"}
              </p>
            </div>
          )}
        </div>
      </div>
      <Navigation />
    </div>
  );
};

export default TodoList;
